import { themeService } from '../../services/ThemeService';
import type { ColorValue } from '../types';

// Re-export from core
export * from '@supply-mover-2-0/core';

// Create default color rule - returns 'default' type which evaluates to null (uses textPrimary)
export function createDefaultColorRule() {
	return {
		type: 'default' as const,
	};
}

// Size mappings for badges, pills, and text
export const SIZE_MAP = {
	xs: {
		fontSize: '0.625rem',
		padding: '0.0625rem 0.375rem',
		iconSize: 10,
	},
	sm: {
		fontSize: '0.75rem',
		padding: '0.125rem 0.5rem',
		iconSize: 12,
	},
	md: {
		fontSize: '0.875rem',
		padding: '0.25rem 0.75rem',
		iconSize: 14,
	},
	lg: {
		fontSize: '1rem',
		padding: '0.375rem 1rem',
		iconSize: 16,
	},
};

// Apply color rules to style
export function applyColorToStyle(
	style: any,
	backgroundColor?: string,
	textColor?: string,
	borderColor?: string,
): any {
	return {
		...style,
		...(backgroundColor && { backgroundColor }),
		...(textColor && { color: textColor }),
		...(borderColor && { borderColor }),
	};
}

/**
 * Evaluates a conditional color rule against a data object
 * @param ruleCondition The conditional color rule to evaluate
 * @param data The data object containing field values
 * @returns true if the condition matches, false otherwise
 */
function evaluateColorCondition(
	ruleCondition: any,
	data: Record<string, any>,
): boolean {
	const fieldName = ruleCondition.field?.fieldName;
	const fieldValue = data[fieldName];

	if (fieldValue == null) {
		return false;
	}

	const value = ruleCondition.value;
	const ignoreCase = ruleCondition.ignoreCase === true;

	// Helper to compare values with optional case insensitivity
	const compareValues = (a: unknown, b: unknown): boolean => {
		if (ignoreCase && typeof a === 'string' && typeof b === 'string') {
			return a.toLowerCase() === b.toLowerCase();
		}
		return a === b;
	};

	switch (ruleCondition.operator) {
		case 'equals':
			return compareValues(fieldValue, value);

		case 'not_equals':
			return !compareValues(fieldValue, value);

		case 'in':
			if (Array.isArray(value)) {
				return value.some((v) => compareValues(fieldValue, v));
			}

			return compareValues(fieldValue, value);

		case 'not_in':
			if (Array.isArray(value)) {
				return !value.some((v) => compareValues(fieldValue, v));
			}

			return !compareValues(fieldValue, value);

		default:
			return false;
	}
}

/**
 * Evaluate color rules with optional data for conditional rules
 * @param rule The color rule configuration
 * @param data Optional data object for evaluating conditional rules
 * @returns ColorValue or null
 */
export function evaluateColorRule(
	rule: any,
	data?: Record<string, any>,
): ColorValue | null {
	if (!rule) {
		return null;
	}

	// Handle dynamic color rules - color comes from a field value
	if (rule.type === 'dynamic' && rule.colorField && data) {
		const colorValue = getNestedFieldValue(data, rule.colorField);
		if (colorValue) {
			return {
				bg: undefined,
				text: colorValue,
			};
		}

		return null;
	}

	// Handle field-based color rules - background color comes from a field value
	// Used for badges/chips where the background color is stored in the data
	if (rule.type === 'field' && rule.fieldName && data) {
		const colorValue = getNestedFieldValue(data, rule.fieldName);
		if (colorValue) {
			return {
				bg: colorValue,
				text: '#ffffff',
			};
		}

		return null;
	}

	// Handle conditional color rules
	if (rule.type === 'conditional' && rule.rules && data != null) {
		for (const ruleCondition of rule.rules) {
			if (evaluateColorCondition(ruleCondition, data)) {
				return ruleCondition.color ?? null;
			}
		}

		// Return default color if no condition matches
		if (rule.defaultColor != null) {
			return rule.defaultColor;
		}

		return null;
	}

	// Handle static color rules
	if (rule.type === 'static' && rule.staticColor) {
		return {
			bg: rule.staticColor.bg,
			text: rule.staticColor.text,
		};
	}

	// Handle theme color rules - references theme color keys
	if (rule.type === 'theme' && rule.themeColor) {
		const themeColors = themeService.getColors();
		const colorKey = rule.themeColor;

		// Check if it's a text color key
		if (colorKey.startsWith('text')) {
			return {
				bg: undefined,
				text: themeColors.values[colorKey],
			};
		}

		// Check if it's a background color key
		if (
			colorKey.startsWith('bg') ||
			colorKey === 'background' ||
			colorKey === 'surface'
		) {
			return {
				bg: themeColors.values[colorKey],
				text: undefined,
			};
		}

		// For other color keys, use as text color
		return {
			bg: undefined,
			text: themeColors.values[colorKey],
		};
	}

	// Handle range-mapping color rules — match numeric value to a range
	if (rule.type === 'range-mapping' && rule.rangeMapping && data != null) {
		// The value to check comes from the field's data source, passed via
		// displayHelpers which resolves it before calling evaluateColorRule.
		// We receive the raw data object, so we need the resolved numeric value.
		// displayHelpers passes `data` which is the full row — the caller should
		// also pass the resolved value. For now, check `data.__resolvedValue`
		// which displayHelpers sets, or fall back to checking all ranges.
		const numValue =
			typeof data.__resolvedValue === 'number'
				? data.__resolvedValue
				: typeof data.__resolvedValue === 'string'
					? parseFloat(data.__resolvedValue)
					: null;

		if (numValue != null && !isNaN(numValue)) {
			for (const range of rule.rangeMapping) {
				const min =
					typeof range.min === 'string' ? parseFloat(range.min) : range.min;
				const max =
					typeof range.max === 'string' ? parseFloat(range.max) : range.max;
				if (!isNaN(min) && !isNaN(max) && numValue >= min && numValue <= max) {
					return range.color ?? null;
				}
			}
		}

		return null;
	}

	// Handle default type
	if (rule.type === 'default') {
		return null; // Use default theme colors
	}

	// Fallback for old format
	return {
		bg: rule?.backgroundColor,
		text: rule?.textColor,
	};
}

// Helper to get nested field value
function getNestedFieldValue(data: any, fieldPath: string): any {
	if (!data || !fieldPath) {
		return null;
	}

	const keys = fieldPath.split('.');
	let value = data;

	for (const key of keys) {
		if (value == null) {
			return null;
		}
		value = value[key];
	}

	return value;
}
